import { useState, useEffect } from 'react';
import { Pencil, Check, X } from 'lucide-react';
import { TargetType, MonthlyRevenueData, MONTHS, calcAllProjections, calcAverageKinerja } from './kinerja.types';

interface ProyeksiTableProps {
  targetType: TargetType;
  monthlyData: MonthlyRevenueData[];
  projectionYears: number[];
  projectionKinerjaPct: number;
  isReadOnly?: boolean;
  isKinerjaPctManuallyEdited?: boolean;
  onProjectionKinerjaPctChange: (pct: number) => void;
  triggerEditMode?: number;
}

const MAX_VISIBLE_YEARS = 5;

function formatRevenue(value: number, type: TargetType): string {
  if (!value) return '-';
  const formatted = value.toLocaleString('id-ID');
  return type === 'volume' ? `${formatted} unit` : formatted;
}

export default function ProyeksiTable({
  targetType,
  monthlyData,
  projectionYears,
  projectionKinerjaPct,
  isReadOnly = false,
  isKinerjaPctManuallyEdited = false,
  onProjectionKinerjaPctChange,
  triggerEditMode,
}: Readonly<ProyeksiTableProps>) {
  const [isEditingPct, setIsEditingPct] = useState(false);
  const [editPctValue, setEditPctValue] = useState<string>(projectionKinerjaPct ? projectionKinerjaPct.toFixed(2) : '');

  const averageKinerja = calcAverageKinerja(monthlyData);

  useEffect(() => {
    setEditPctValue(projectionKinerjaPct ? projectionKinerjaPct.toFixed(2).replace('.', ',') : '');
  }, [projectionKinerjaPct]);

  useEffect(() => {
    if (triggerEditMode) {
      setIsEditingPct(true);
    }
  }, [triggerEditMode]);

  const allProjections = calcAllProjections(monthlyData, projectionKinerjaPct, projectionYears);

  const visibleYears = projectionYears.slice(0, MAX_VISIBLE_YEARS);
  const hasMore = projectionYears.length > MAX_VISIBLE_YEARS;

  const handleEditConfirm = () => {
    const parsed = Number.parseFloat(editPctValue.replace(',', '.'));

    if (!Number.isNaN(parsed)) {
      onProjectionKinerjaPctChange(parsed);
    }

    setIsEditingPct(false);
  };

  const handleEditCancel = () => {
    setEditPctValue(projectionKinerjaPct ? projectionKinerjaPct.toFixed(2).replace('.', ',') : '');

    setIsEditingPct(false);
  };

  const yearTotals = visibleYears.reduce<Record<number, number>>((acc, year) => {
    let sum = 0;
    for (let m = 1; m <= 12; m++) {
      sum += allProjections[year]?.[m] ?? 0;
    }
    acc[year] = sum;
    return acc;
  }, {});

  return (
    <div id="proyeksi-table-container" className="mt-8">
      <h3 className="text-base font-semibold text-gray-800 dark:text-white text-center mb-4">
        Proyeksi Pertumbuhan Kinerja Perusahaan yang Ditargetkan Sesuai dengan Visi Perusahaan
      </h3>

      {/* % Kinerja header */}
      <div className="flex items-center gap-3 mb-3">
        <span className="text-sm font-medium text-gray-700 dark:text-white">%Kinerja</span>
        {!isReadOnly && !isEditingPct && (
          <button
            title="Edit persentase kenaikan target"
            onClick={() => setIsEditingPct(true)}
            className="flex items-center gap-1.5 px-3 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-full hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 transition-colors"
          >
            <Pencil size={12} />
            Edit Kenaikan Target Di sini
          </button>
        )}
        {isEditingPct && (
          <div className="flex items-center gap-2">
            <input
              id="edit-pct-input"
              type="text"
              inputMode="decimal"
              title="Persentase kinerja target"
              value={editPctValue}
              onChange={e => {
                const value = e.target.value;
                if (/^\d*[,.]?\d*$/.test(value)) {
                  setEditPctValue(value.replace('.', ','));
                }
              }}
              className="w-24 px-2 py-1 text-sm border border-blue-400 dark:border-blue-500 dark:bg-gray-700 dark:text-white rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              autoFocus
            />
            <span className="text-sm text-gray-500 dark:text-gray-400">%</span>
            <button title="Konfirmasi" onClick={handleEditConfirm} className="p-1 text-green-600 hover:text-green-700">
              <Check size={14} />
            </button>
            <button title="Batal" onClick={handleEditCancel} className="p-1 text-red-500 hover:text-red-600">
              <X size={14} />
            </button>
          </div>
        )}
        {averageKinerja !== null && !isEditingPct && (
          <span
            className={`text-xs ${
              !isKinerjaPctManuallyEdited ? 'text-blue-500 font-medium' : 'text-gray-400 dark:text-gray-500'
            }`}
          >
            {!isKinerjaPctManuallyEdited
              ? `(auto-filled dari rata-rata: ${averageKinerja.toFixed(2)}%)`
              : `(rata-rata aktual: ${averageKinerja.toFixed(2)}%)`}
          </span>
        )}
      </div>

      <div className="flex items-center gap-1 mb-2">
        <span className="text-xs text-gray-400 dark:text-gray-500">&#9432;</span>
        <span className="text-xs text-gray-500 dark:text-gray-400">Target Omzet 5 Tahun Ke Depan</span>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="min-w-[640px] w-full text-sm">
          <thead>
            {/* % Kinerja row */}
            <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <th className="px-4 py-2 text-left font-semibold text-gray-700 dark:text-white w-28">% Kinerja</th>
              {visibleYears.map(year => (
                <th key={year} className="px-4 py-2 text-center font-semibold text-gray-500 dark:text-gray-300 text-xs">
                  {projectionKinerjaPct
                    ? `${projectionKinerjaPct.toLocaleString('id-ID', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}%`
                    : '-'}
                </th>
              ))}
              {hasMore && <th className="px-4 py-2 text-center text-gray-400 dark:text-gray-500 text-xs">....</th>}
            </tr>
            {/* Tahun / Target Revenue header */}
            <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-300 dark:border-gray-600">
              <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-white">Tahun</th>
              {visibleYears.map(year => (
                <th key={year} className="px-4 py-3 text-center font-semibold text-gray-700 dark:text-white">
                  Target Omzet {year}
                </th>
              ))}
              {hasMore && (
                <th className="px-4 py-3 text-center text-gray-500 dark:text-gray-300 font-semibold">
                  Target Omzet (...)
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {MONTHS.map((monthName, idx) => {
              const month = idx + 1;

              return (
                <tr
                  key={month}
                  className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50/50 dark:hover:bg-gray-700/50"
                >
                  <td className="px-4 py-2 font-medium text-gray-700 dark:text-white">{monthName}</td>
                  {visibleYears.map(year => {
                    const rev = allProjections[year]?.[month] ?? 0;
                    return (
                      <td key={year} className="px-4 py-2 text-center text-gray-600 dark:text-gray-300">
                        {rev ? formatRevenue(rev, targetType) : '-'}
                      </td>
                    );
                  })}
                  {hasMore && <td className="px-4 py-2 text-center text-gray-400 dark:text-gray-500">...</td>}
                </tr>
              );
            })}

            {/* Total row */}
            <tr className="bg-gray-50 dark:bg-gray-800 border-t-2 border-gray-300 dark:border-gray-600 font-bold">
              <td className="px-4 py-3 font-bold text-gray-800 dark:text-white">Total</td>
              {visibleYears.map(year => {
                const total = yearTotals[year] ?? 0;
                return (
                  <td key={year} className="px-4 py-3 text-center font-bold text-gray-800 dark:text-white">
                    {total ? formatRevenue(total, targetType) : '-'}
                  </td>
                );
              })}
              {hasMore && <td className="px-4 py-3 text-center text-gray-400 dark:text-gray-500">...</td>}
            </tr>

            {/* Rata-rata row */}
            <tr className="bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 font-semibold">
              <td className="px-4 py-3 font-bold text-gray-800 dark:text-white">Rata-rata</td>
              {visibleYears.map(year => {
                const total = yearTotals[year] ?? 0;
                const avg = total ? Math.round(total / 12) : 0;
                return (
                  <td key={year} className="px-4 py-3 text-center font-semibold text-gray-800 dark:text-white">
                    {avg ? formatRevenue(avg, targetType) : '-'}
                  </td>
                );
              })}
              {hasMore && <td className="px-4 py-3 text-center text-gray-400 dark:text-gray-500">...</td>}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
